import React, { useState, useEffect } from 'react';
import './PadelNewsTab.css';
import MatchDetailModal from './MatchDetailModal';

// Backend serves the real Premier Padel schedule: REST-sourced full draw
// (all statuses) merged with WebSocket live score deltas server-side.
// See services/premierPadelService.js in the backend for details.
const BACKEND_API = process.env.REACT_APP_API_URL || 'https://player-notes-backend-production.up.railway.app/api';

// Country code to flag emoji mapping (2-letter and 3-letter codes)
const countryFlags = {
  'AR': '🇦🇷', 'ARG': '🇦🇷',
  'BR': '🇧🇷', 'BRA': '🇧🇷',
  'ES': '🇪🇸', 'ESP': '🇪🇸',
  'PT': '🇵🇹', 'POR': '🇵🇹',
  'IT': '🇮🇹', 'ITA': '🇮🇹',
  'FR': '🇫🇷', 'FRA': '🇫🇷',
  'US': '🇺🇸', 'USA': '🇺🇸',
  'MX': '🇲🇽', 'MEX': '🇲🇽',
  'CL': '🇨🇱', 'CHI': '🇨🇱',
  'CO': '🇨🇴', 'COL': '🇨🇴',
  'PE': '🇵🇪', 'PER': '🇵🇪',
  'UY': '🇺🇾', 'URY': '🇺🇾',
  'GB': '🇬🇧', 'GBR': '🇬🇧',
  'DE': '🇩🇪', 'GER': '🇩🇪',
  'SE': '🇸🇪', 'SWE': '🇸🇪',
  'NO': '🇳🇴', 'NOR': '🇳🇴',
  'DK': '🇩🇰', 'DEN': '🇩🇰',
  'NL': '🇳🇱', 'NED': '🇳🇱',
  'AU': '🇦🇺', 'AUS': '🇦🇺',
  'NZ': '🇳🇿', 'NZL': '🇳🇿',
  'ZA': '🇿🇦', 'RSA': '🇿🇦',
  'IN': '🇮🇳', 'IND': '🇮🇳',
  'QA': '🇶🇦', 'QAT': '🇶🇦',
  'AE': '🇦🇪', 'UAE': '🇦🇪',
  'KW': '🇰🇼', 'KUW': '🇰🇼',
  'EG': '🇪🇬', 'EGY': '🇪🇬',
  'BE': '🇧🇪', 'BEL': '🇧🇪',
  'CH': '🇨🇭', 'SUI': '🇨🇭',
  'PY': '🇵🇾', 'PAR': '🇵🇾',
  'EC': '🇪🇨', 'ECU': '🇪🇨',
  'PL': '🇵🇱', 'POL': '🇵🇱',
  'FI': '🇫🇮', 'FIN': '🇫🇮',
  'CZ': '🇨🇿', 'CZE': '🇨🇿'
};

const getCountryFlag = (countryCode) => {
  if (!countryCode) return '';
  const code = countryCode.toUpperCase().trim();
  return countryFlags[code] || '';
};

// Countdown as D : HH : MM : SS to match the official site's display
const formatCountdown = (ms) => {
  if (ms <= 0) return '00 : 00 : 00 : 00';
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(days)} : ${pad(hours)} : ${pad(minutes)} : ${pad(seconds)}`;
};

// Same breakdown as formatCountdown, but as separate numbers for the
// Next Tournament banner's boxed Days/Hours/Mins/Secs display.
const getCountdownParts = (ms) => {
  const clamped = Math.max(0, ms);
  const totalSeconds = Math.floor(clamped / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60
  };
};

// "28 Sep - 04 Oct" - single line, cross-month-safe, no year (tournament
// dates are always near-term).
const formatDateRange = (startIso, endIso) => {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';
  const fmt = (d) => `${String(d.getUTCDate()).padStart(2, '0')} ${d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })}`;
  return `${fmt(start)} - ${fmt(end)}`;
};

export default function PadelNewsTab() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [selectedDate, setSelectedDate] = useState(0);
  const [dateTabs, setDateTabs] = useState([]);
  const [now, setNow] = useState(new Date());
  const [tournamentInfo, setTournamentInfo] = useState(null);

  // Generate date tabs (today + next 7 days)
  useEffect(() => {
    const tabs = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 8; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      tabs.push(date);
    }
    setDateTabs(tabs);
  }, []);

  const fetchMatches = async () => {
    try {
      const response = await fetch(`${BACKEND_API}/matches/tournament-draws`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.matches)) {
        setMatches(data.matches);
        setTournamentInfo(data.tournamentInfo || null);
        setConnectionStatus('connected');
        setLoading(false);
        setError(null);
      } else {
        throw new Error('Invalid response format from backend');
      }
    } catch (err) {
      console.error('❌ Error fetching matches:', err);
      setConnectionStatus('error');
      setError(`Connection error: ${err.message}`);
      setLoading(false);
    }
  };

  // Initial load and 30-second auto-refresh
  useEffect(() => {
    fetchMatches();
    const interval = setInterval(fetchMatches, 30000);
    return () => clearInterval(interval);
  }, []);

  // Tick every second for live countdown timers
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Get matches for selected date
  const getMatchesForDate = (dateIndex) => {
    const date = dateTabs[dateIndex];
    if (!date) return [];

    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    return matches.filter(m => {
      const matchDate = new Date(m.matchDate);
      return matchDate >= startOfDay && matchDate <= endOfDay;
    }).sort((a, b) => new Date(a.matchDate) - new Date(b.matchDate));
  };

  // Split into the 3 required sections (+ a bonus "later" section).
  //
  // Live and Starting Soon are computed from the FULL, unfiltered match
  // list and intentionally ignore which date tab is selected - "what's
  // live right now" / "what's starting within 24 real hours" is a
  // relative-to-this-instant concept, not a "which calendar box does the
  // printed matchDate fall into" one. If they were date-filtered like
  // Completed/Upcoming, a match still genuinely live or about to start
  // would disappear entirely the moment the viewer's LOCAL calendar day
  // rolls past the match's UTC matchDate - which happens for any viewer
  // east of UTC well before the match (or even the tournament's day)
  // actually ends, and the date tabs only ever offer "today" (local) as
  // the earliest option, so there's no way to navigate back to it either.
  // Confirmed live: a match with status 'live' and matchDate on Aug 30
  // UTC vanished from every section once the viewer's local date ticked
  // over to Aug 31, despite the match still actually being in progress.
  const separateMatches = (dateFilteredList) => {
    const dayFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000);

    const live = matches.filter(m => m.status === 'live');

    // Note: no lower bound on matchTime here. Live padel events routinely
    // run behind their printed schedule - a match's nominal matchDate can
    // slip into the past while its status is still 'scheduled' (it just
    // hasn't been called to court yet). Requiring matchTime > now would
    // make that match vanish from every section: not live, not completed,
    // and excluded here for being "in the past" - it needs to stay visible
    // as "up next" regardless of how overdue the printed time is.
    const startingSoon = matches.filter(m => {
      if (m.status !== 'scheduled') return false;
      const matchTime = new Date(m.matchDate);
      return matchTime <= dayFromNow;
    });

    // Completed/Upcoming are genuinely "browse by calendar day" concepts,
    // so these stay scoped to whichever date tab is selected.
    const completed = dateFilteredList.filter(m => m.status === 'completed');

    const upcoming = dateFilteredList.filter(m => {
      if (m.status !== 'scheduled') return false;
      const matchTime = new Date(m.matchDate);
      return matchTime > dayFromNow;
    });

    return { live, completed, startingSoon, upcoming };
  };

  // Group matches by court (e.g. "PRO PADEL 1" / "PRO PADEL 2") - this is
  // exactly how the official site groups matches within a tournament.
  const groupMatches = (matchList) => {
    const grouped = {};
    matchList.forEach(match => {
      const key = match.courtGroup || match.tournament || 'Matches';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(match);
    });
    return grouped;
  };

  const getConnectionColor = () => {
    switch (connectionStatus) {
      case 'connected': return '#27ae60';
      case 'connecting': return '#f39c12';
      case 'disconnected':
      case 'error': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const formatDateTab = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    return { day, month };
  };

  const selectedDateMatches = getMatchesForDate(selectedDate);
  const { live, completed, startingSoon, upcoming } = separateMatches(selectedDateMatches);

  // ---- Reusable pieces -------------------------------------------------

  const EmptyState = ({ text }) => (
    <div className="pn-empty-state">
      {text}
    </div>
  );

  const TeamPlayers = ({ team, isServing, highlight }) => {
    const players = team.players && team.players.length > 0
      ? team.players
      : [{ name: team.name, rank: team.ranking, countryCode: team.countryCode }];

    return (
      <div className="pn-player">
        {players.map((p, i) => (
          <div
            key={i}
            className={`pn-player-line ${highlight ? 'pn-serving-player' : ''}`}
            style={{ marginBottom: i < players.length - 1 ? '4px' : '0' }}
          >
            {isServing && i === 0 && <span>🎾</span>}
            {p.countryCode && <span>{getCountryFlag(p.countryCode)}</span>}
            <span className="pn-player-name">{p.name || 'TBD'}</span>
            {(p.rank !== null && p.rank !== undefined) && (
              <span className="pn-player-rank">({p.rank})</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const ScoreDisplay = ({ team, playerIdx }) => (
    <div className="pn-score">
      {[1, 2, 3, 4, 5].map(setNum => {
        const val = team.score?.[`set${setNum}`];
        if (val === null || val === undefined) return null;
        return (
          <div key={setNum} className="pn-score-set">
            {val}
          </div>
        );
      })}
      {team.score?.points && (
        <div className={`pn-score-points ${playerIdx === 0 ? 'pn-score-points-p1' : 'pn-score-points-p2'}`}>
          {team.score.points}
        </div>
      )}
    </div>
  );

  const BroadcastLinks = ({ match }) => {
    if (!match.broadcastUrl && !match.ticketUrl) return null;
    return (
      <div className="pn-broadcast-links">
        {match.broadcastUrl && (
          <a
            href={match.broadcastUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="pn-broadcast-link"
          >
            📺 Watch
          </a>
        )}
        {match.ticketUrl && (
          <a
            href={match.ticketUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="pn-broadcast-link"
          >
            🎟️ Tickets
          </a>
        )}
      </div>
    );
  };

  const MatchCard = ({ match, variant }) => {
    const team1 = match.team1 || {};
    const team2 = match.team2 || {};
    const currentServer = match.liveScore?.currentServer;
    const servingTeam = currentServer === 'player1' ? team1 : (currentServer === 'player2' ? team2 : null);
    const servingNames = servingTeam
      ? (servingTeam.players && servingTeam.players.length > 0
          ? servingTeam.players.map(p => p.name).filter(Boolean).join(' & ')
          : servingTeam.name)
      : null;

    return (
      <div
        onClick={() => { setSelectedMatch(match); setShowDetail(true); }}
        className="pn-match-card"
      >
        {/* Badge row */}
        <div className="pn-badge-row">
          <span className="pn-badge">
            {match.courtGroup || match.circuit || 'PRO'}{match.round ? ` | ${match.round}` : ''}
          </span>

          {variant === 'live' && (
            <span className="pn-status-live">🔴 LIVE</span>
          )}
          {variant === 'completed' && (
            <span className="pn-status-completed">✅ FINISHED</span>
          )}
          {variant === 'soon' && (
            <span className="pn-status-soon">
              {new Date(match.matchDate) - now > 0
                ? `Starts In: ${formatCountdown(new Date(match.matchDate) - now)}`
                : '🎾 Up Next - Running Late'}
            </span>
          )}
          {variant === 'upcoming' && (
            <span className="pn-status-upcoming">
              {new Date(match.matchDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              {match.startTime ? ` · ${match.startTime}` : ''}
            </span>
          )}
          {match.header && (
            <span className="pn-match-header-text">{match.header}</span>
          )}
        </div>

        {/* Players + score */}
        <div className="pn-match-body">
          {match.duration && (variant === 'live' || variant === 'completed') && (
            <div className="pn-duration">
              ⏱️ Duration: {match.duration}
            </div>
          )}

          {variant === 'live' && servingNames && (
            <div className="pn-serving-banner">
              🎾 Serving: {servingNames}
            </div>
          )}

          {[[team1, 'player1', 0], [team2, 'player2', 1]].map(([team, key, idx]) => {
            const isServing = currentServer === key;
            return (
              <div
                key={key}
                className={`pn-team-row ${idx === 0 ? 'pn-team-row-first' : ''} ${currentServer && !isServing ? 'pn-not-serving' : ''}`}
              >
                <TeamPlayers team={team} isServing={isServing} highlight={isServing} />
                {(variant === 'live' || variant === 'completed') && <ScoreDisplay team={team} playerIdx={idx} />}
              </div>
            );
          })}
        </div>

        {variant === 'completed' && match.winnerDisplay && (
          <div className="pn-winner-banner">
            👑 Winner: <span style={{ fontWeight: '700' }}>{match.winnerDisplay}</span>
          </div>
        )}

        <BroadcastLinks match={match} />

        <div className="pn-location-row">
          {match.location && <span>📍 {match.location}</span>}
          {match.venue && <span>🏟️ {match.venue}</span>}
        </div>
      </div>
    );
  };

  const MatchSection = ({ title, color, icon, matchList, variant, emptyText }) => {
    const grouped = groupMatches(matchList);
    const groupKeys = Object.keys(grouped);

    return (
      <div className="pn-section">
        <div className="pn-section-title" style={{ color }}>
          {icon} {title}
        </div>

        {groupKeys.length === 0 ? (
          <EmptyState text={emptyText} />
        ) : (
          groupKeys.map(groupName => (
            <div key={groupName} className="pn-group">
              <div className="pn-group-header">
                {groupName}
              </div>
              <div className="pn-group-body">
                {grouped[groupName].map((match, idx) => (
                  <div
                    key={match.externalMatchId || idx}
                    className="pn-match-row"
                  >
                    <MatchCard match={match} variant={variant} />
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <div className="pn-page">
      {/* Header */}
      <div className="pn-header-row">
        <div>
          <h2 className="pn-header-title">
            🏆 Premier Padel Matches
          </h2>
          <div className="pn-connection-row" style={{ color: getConnectionColor() }}>
            <span className="pn-connection-dot" style={{ background: getConnectionColor() }} />
            {connectionStatus === 'connected' && '🟢 Live'}
            {connectionStatus === 'connecting' && '🟡 Connecting...'}
            {connectionStatus === 'disconnected' && '🔴 Disconnected'}
            {connectionStatus === 'error' && '🔴 Error'}
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="pn-error-box">
          ⚠️ {error}
        </div>
      )}

      {/* Next Tournament countdown - only while the tournament we're
          tracking genuinely hasn't started yet ("Upcoming"/status "N").
          Once it goes live, the match sections below already show it. */}
      {tournamentInfo && tournamentInfo.status === 'N' && tournamentInfo.start_date_utc && (() => {
        const parts = getCountdownParts(new Date(tournamentInfo.start_date_utc) - now);
        const name = tournamentInfo.name || tournamentInfo.display_name || 'Premier Padel';
        const dateRange = formatDateRange(tournamentInfo.start_date_utc, tournamentInfo.end_date_utc);
        const link = tournamentInfo.ticket_url || null;
        const Wrapper = link ? 'a' : 'div';
        const wrapperProps = link ? { href: link, target: '_blank', rel: 'noopener noreferrer' } : {};

        return (
          <Wrapper className="pn-next-tournament" {...wrapperProps}>
            {link && <span className="pn-next-tournament-arrow">→</span>}
            {dateRange && <div className="pn-next-tournament-dates">{dateRange}</div>}
            <div className="pn-next-tournament-name">{name}</div>
            <div className="pn-countdown-label">Starting In</div>
            <div className="pn-countdown-units">
              <div className="pn-countdown-unit">
                <div className="pn-countdown-unit-value">{String(parts.days).padStart(2, '0')}</div>
                <div className="pn-countdown-unit-label">Days</div>
              </div>
              <div className="pn-countdown-unit">
                <div className="pn-countdown-unit-value">{String(parts.hours).padStart(2, '0')}</div>
                <div className="pn-countdown-unit-label">Hours</div>
              </div>
              <div className="pn-countdown-unit">
                <div className="pn-countdown-unit-value">{String(parts.minutes).padStart(2, '0')}</div>
                <div className="pn-countdown-unit-label">Mins</div>
              </div>
              <div className="pn-countdown-unit">
                <div className="pn-countdown-unit-value">{String(parts.seconds).padStart(2, '0')}</div>
                <div className="pn-countdown-unit-label">Secs</div>
              </div>
            </div>
          </Wrapper>
        );
      })()}

      {/* Date Tabs */}
      <div className="pn-date-tabs">
        {dateTabs.map((date, idx) => {
          const { day, month } = formatDateTab(date);
          const isToday = idx === 0;
          const isSelected = selectedDate === idx;

          return (
            <button
              key={idx}
              onClick={() => setSelectedDate(idx)}
              className={`pn-date-tab ${isSelected ? 'selected' : ''}`}
            >
              <div className="pn-date-tab-day">{day}</div>
              <div className="pn-date-tab-month">{month}</div>
              {isToday && <div className="pn-date-tab-today">Today</div>}
            </button>
          );
        })}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="pn-loading">
          <div className="pn-loading-icon">⏳</div>
          <div style={{ fontSize: '16px' }}>Loading Premier Padel matches...</div>
        </div>
      )}

      {/* The 3 required sections (always visible) + bonus Upcoming section */}
      {!loading && (
        <div>
          <MatchSection
            title="Live Scores"
            color="#e74c3c"
            icon="🔴"
            matchList={live}
            variant="live"
            emptyText="No live matches right now"
          />

          <MatchSection
            title="Completed Matches"
            color="#27ae60"
            icon="✅"
            matchList={completed}
            variant="completed"
            emptyText="No completed matches today"
          />

          <MatchSection
            title="Starting Soon"
            color="#f39c12"
            icon="⏱️"
            matchList={startingSoon}
            variant="soon"
            emptyText="No matches starting in the next 24 hours"
          />

          <MatchSection
            title="Upcoming"
            color="#2f4ba2"
            icon="📅"
            matchList={upcoming}
            variant="upcoming"
            emptyText="No further matches scheduled"
          />
        </div>
      )}

      {/* Match Detail Modal */}
      {showDetail && selectedMatch && (
        <MatchDetailModal
          match={selectedMatch}
          onClose={() => setShowDetail(false)}
        />
      )}
    </div>
  );
}
